/**
 * Raid battle presentation — fantasy raid pack + UnitRig + timed attack VFX.
 */
import Phaser from "phaser";
import {
  ArtPipeline,
  affiliationForBinaryTeam,
} from "../../../art";
import { raidCatalog, hexToColor } from "../../../data/raid-catalog";
import { t } from "../../../data/strings";
import {
  VfxPlayer,
  attackTravelSec,
  coneTelegraphPoints,
  leapTelegraphFillProgress,
  lineTelegraphQuad,
  playCircleAoeCastCue,
  playCircleAoeImpact,
  playConeAoeCastCue,
  playConeAoeImpact,
  playFloatText,
  playTimedAttackFx,
  resolveAttackFx,
  resolveAttackOrigin,
  resolveHitPreset,
  sampleKnockFlight,
  telegraphFillProgress,
  visualTravelMs,
  type AttackFxRoleConfig,
  type AttackFxTables,
  type Pt,
  type RaidAoeTheme,
} from "../../../vfx";
import {
  UnitRig,
  createDisplayFacingState,
  createHpBar,
  raidGripPack,
  stepDisplayFacing,
  updateHpBar,
  type DisplayFacingState,
  type RigTables,
} from "../../../view/unit-rig";
import type { RaidBattleState, RaidSimEvent, RaidUnit } from "../raid-rules";
import type { GestureVisual } from "../raid-gesture";
import { gestureSelectTargets } from "../raid-gesture";
import { resolveBossAggroHudTarget } from "../raid-aggro";
import { isBossDormant } from "../raid-assault";
import { bruteVariantLookForUnit } from "../raid-brute-variant";
import { raidFreezeDiag } from "../raid-freeze-diag";
import type { SimStageDef } from "../sim-lab/sim-lab-types";
import {
  ALLY_STUN_ICON_GLYPH,
  STUN_BLINK_TINT,
  allyStunIconLocalPos,
  stunBlinkAlphas,
  stunBlinkOn,
} from "./raid-stun-presentation";
import {
  enemyEscalateBadgeLocalPos,
  enemyEscalateBadgeText,
  shouldShowEnemyEscalateBadge,
} from "./raid-escalate-presentation";
import { getEscalateStacks } from "../raid-escalate";
import {
  RaidEvent,
  raidDeathFx,
  swingSecForBoss,
  swingSecForClass,
} from "../raid-rules";
import { RaidDepth, raidBodyDepth, raidBossDepth } from "./raid-layers";
import {
  DEFAULT_SPEECH_BUBBLE,
  playSpeechBubble,
  type SpeechBubbleHandle,
  type SpeechBubbleStyle,
} from "./speech-bubble";
import { stepDisplayPos } from "../../portal-ffa/view/presentation-smooth";
import { airborneTiltRad, easeInOutCubic, parabola01, sampleAirborneDepth } from "../../../vfx/motion-curves";

const VFX_LAYER = { depth: RaidDepth.fx, floatDepth: RaidDepth.float } as const;
const WALK_ENTER = 18;
const RIG = raidCatalog.view.rig as unknown as RigTables;
const ATTACK_FX = raidCatalog.view.attackFx as AttackFxTables;
/** Map key for boss speech bubbles (boss has no party unit id). */
const BOSS_SPEECH_UNIT_ID = -1;

type MarkedShotSkillFx = Partial<AttackFxRoleConfig> & {
  markPreset?: string;
  boltScale?: number;
};

type ShieldBashSkillFx = Partial<AttackFxRoleConfig>;

function markedShotFx(): MarkedShotSkillFx | null {
  const row = (
    raidCatalog.view as {
      skillFx?: { marked_shot?: MarkedShotSkillFx };
    }
  ).skillFx?.marked_shot;
  return row ?? null;
}

function shieldBashFx(): ShieldBashSkillFx | null {
  const row = (
    raidCatalog.view as {
      skillFx?: { shield_bash?: ShieldBashSkillFx };
    }
  ).skillFx?.shield_bash;
  return row ?? null;
}

const MAP_H = raidCatalog.balance.map.height;
const FOLLOW_HZ = 16;
/** Boss float/hit FX: surface fraction of radius toward attacker. */
const BOSS_HIT_SURFACE = 0.85;

const FACING_CFG = (
  raidCatalog.view as {
    facing?: {
      displayAimTurnRate?: number;
      holdRad?: number;
      dwellFrames?: number;
    };
  }
).facing;
const DISPLAY_AIM_TURN_RATE = Number(FACING_CFG?.displayAimTurnRate) || 3.5;
const FACING_HOLD_RAD = Number(FACING_CFG?.holdRad) || 0.32;
const FACING_DWELL_FRAMES = Math.max(
  0,
  Math.floor(Number(FACING_CFG?.dwellFrames) || 2),
);
const FACING_STEP_OPTS = {
  holdRad: FACING_HOLD_RAD,
  dwellFrames: FACING_DWELL_FRAMES,
} as const;

/**
 * Impact near boss rim facing the attacker.
 * Concurrent damage float fan-out is handled in `playFloatText` (all targets).
 */
function bossHitImpactPos(
  bossX: number,
  bossY: number,
  bossRadius: number,
  fromX: number,
  fromY: number,
): { x: number; y: number } {
  const dx = fromX - bossX;
  const dy = fromY - bossY;
  const len = Math.hypot(dx, dy);
  const surface = bossRadius * BOSS_HIT_SURFACE;
  if (len < 1e-3) {
    return { x: bossX, y: bossY - surface };
  }
  const inv = 1 / len;
  return {
    x: bossX + dx * inv * surface,
    y: bossY + dy * inv * surface,
  };
}
const FOLLOW_SNAP = 56;
/** Boss leap display apex (screen lift) — sim XY unchanged. */
const LEAP_APEX = 96;
/** Boss leap airborne scale boost toward camera at apex. */
const LEAP_SCALE_BOOST = 0.42;
const LEAP_TILT_RAD = 0.18;
/** Hero knock perspective (tanker-toward-camera). */
const KNOCK_SCALE_BOOST = 0.55;
const KNOCK_TILT_RAD = 0.48;
const KNOCK_HEAVY_APEX = 40;
const KNOCK_TRAIL_SEC = 0.05;

function demoBossDef() {
  const id = raidCatalog.bosses.demoBossId as keyof typeof raidCatalog.bosses.bosses;
  return raidCatalog.bosses.bosses[id];
}

type BossSkillFx = {
  castAnim?: string;
  openVfx?: string;
  impactVfx?: string;
  areaFlash?: boolean;
  areaFlashMs?: number;
  danger?: number;
};

function skillPresentation(
  skillId: "slam" | "brass" | "leap" | "charge",
): BossSkillFx {
  const b = demoBossDef();
  switch (skillId) {
    case "slam":
      return b.slam;
    case "brass":
      return b.brass;
    case "leap":
      return b.leap;
    case "charge":
      // Red corridor flash — leap severity theme (charge DangerZone is red).
      return b.brass;
    default: {
      const _exhaustive: never = skillId;
      return _exhaustive;
    }
  }
}

/** AoE flash palette — charge uses leap (red severity) theme. */
function aoeThemeFor(
  skillId: "slam" | "brass" | "leap" | "charge",
): RaidAoeTheme {
  if (skillId === "charge") return "leap";
  return skillId;
}

function playSkillAreaImpact(
  scene: Phaser.Scene,
  kind: "circle" | "cone",
  opts: {
    x: number;
    y: number;
    radius?: number;
    range?: number;
    coneDeg?: number;
    aimAngle?: number;
    theme: RaidAoeTheme;
    skill: BossSkillFx;
    phase: "open" | "impact";
    offsetX?: number;
    offsetY?: number;
  },
): void {
  if (opts.skill.areaFlash === false) return;
  const depth = RaidDepth.aoeFx;
  const durationMs = opts.skill.areaFlashMs ?? 720;
  const theme = opts.theme;
  const x = opts.x + (opts.offsetX ?? 0);
  const y = opts.y + (opts.offsetY ?? 0);
  if (kind === "cone") {
    if (opts.phase === "open") {
      playConeAoeCastCue(
        scene,
        x,
        y,
        opts.aimAngle ?? 0,
        opts.range ?? 100,
        opts.coneDeg ?? 70,
        theme,
        { depth },
      );
    } else {
      playConeAoeImpact(
        scene,
        x,
        y,
        opts.aimAngle ?? 0,
        opts.range ?? 100,
        opts.coneDeg ?? 70,
        theme,
        { depth, durationMs },
      );
    }
    return;
  }
  const radius = opts.radius ?? opts.range ?? 64;
  if (opts.phase === "open") {
    playCircleAoeCastCue(scene, x, y, radius, theme, { depth });
  } else {
    playCircleAoeImpact(scene, x, y, radius, theme, {
      depth,
      durationMs,
    });
  }
}

type UnitView = {
  root: Phaser.GameObjects.Container;
  chrome: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig;
  /** Soft ground ellipse while knock flight lifts the body (rig shadow hidden). */
  groundShadow: Phaser.GameObjects.Ellipse;
  hpBg: Phaser.GameObjects.Rectangle;
  hpGhost: Phaser.GameObjects.Rectangle;
  hpFill: Phaser.GameObjects.Rectangle;
  skillBg: Phaser.GameObjects.Rectangle | null;
  skillFill: Phaser.GameObjects.Rectangle | null;
  /** Ally AoE stun glyph under HP (left); null for enemies. */
  stunIcon: Phaser.GameObjects.Text | null;
  /** Enemy escalate (맹공) badge under float HP; null for allies / boss root. */
  escalateBadge: Phaser.GameObjects.Text | null;
  /** Local Y of HP bar center (for stun / escalate icon layout). */
  hpY: number;
  baseSize: number;
  barW: number;
  role: string;
  /** Sprite anim id (usually same as role; kept for view glue). */
  animRole: string;
  /** ArtPipeline affiliation key (team or sticky brute variant). */
  affiliationKey: string;
  team: 0 | 1;
  dying: boolean;
  deathAnimTime: number;
  deathDuration: number;
  /** Accrued seconds since last knock debris trail spawn. */
  knockTrailAcc: number;
};

/** Optional world offset — Sim Lab A/B places lane B beside A. Stage = Sim pad. */
export type RaidPresentationOpts = {
  offsetX?: number;
  offsetY?: number;
  stage?: SimStageDef;
};

export class RaidPresentation {
  private readonly scene: Phaser.Scene;
  private readonly ox: number;
  private readonly oy: number;
  private readonly stage: SimStageDef | null;
  private readonly vfx: VfxPlayer;
  private readonly views = new Map<number, UnitView>();
  private readonly prevAttacking = new Map<number, boolean>();
  private readonly unitFacing = new Map<number, DisplayFacingState>();
  private bossFacing: DisplayFacingState = createDisplayFacingState();
  private bossRoot!: Phaser.GameObjects.Container;
  private bossBody!: Phaser.GameObjects.Image;
  private bossRig: UnitRig | null = null;
  private overlay!: Phaser.GameObjects.Graphics;
  private gestureGfx!: Phaser.GameObjects.Graphics;
  private pinGfx!: Phaser.GameObjects.Graphics;
  private aggroGfx!: Phaser.GameObjects.Graphics;
  private gestureVisual: GestureVisual = { kind: "none" };
  private stageFloor!: Phaser.GameObjects.GameObject;
  private playMaskGfx: Phaser.GameObjects.Graphics | null = null;
  private disposed = false;
  private hitBudget = 24;
  private trailBudget = { left: 8 };
  private lastLeapTravelT = -1;
  private readonly prevKnockActive = new Map<number, boolean>();
  private readonly speechBubbles = new Map<number, SpeechBubbleHandle>();
  private bossDying = false;
  private bossDeathAnimTime = 0;
  private bossGroundShadow: Phaser.GameObjects.Ellipse | null = null;
  private bossBaseScale = 1;
  private bossLandSquashPending = false;

  constructor(scene: Phaser.Scene, opts?: RaidPresentationOpts) {
    this.scene = scene;
    this.ox = opts?.offsetX ?? 0;
    this.oy = opts?.offsetY ?? 0;
    this.stage = opts?.stage ?? null;
    this.vfx = new VfxPlayer(scene);
  }

  private wx(x: number): number {
    return x + this.ox;
  }

  private wy(y: number): number {
    return y + this.oy;
  }

  /** Display root is world-placed; attack/heal math stays in sim-local space. */
  private toLocal(p: { x: number; y: number }): { x: number; y: number } {
    return { x: p.x - this.ox, y: p.y - this.oy };
  }

  private playVfx(
    id: string,
    ctx: Parameters<VfxPlayer["play"]>[1],
  ): void {
    this.vfx.play(id, {
      ...ctx,
      x: typeof ctx.x === "number" ? ctx.x + this.ox : ctx.x,
      y: typeof ctx.y === "number" ? ctx.y + this.oy : ctx.y,
      x1: typeof ctx.x1 === "number" ? ctx.x1 + this.ox : ctx.x1,
      y1: typeof ctx.y1 === "number" ? ctx.y1 + this.oy : ctx.y1,
    });
  }

  private skillImpact(
    kind: "circle" | "cone",
    opts: {
      x: number;
      y: number;
      radius?: number;
      range?: number;
      coneDeg?: number;
      aimAngle?: number;
      theme: RaidAoeTheme;
      skill: BossSkillFx;
      phase: "open" | "impact";
    },
  ): void {
    playSkillAreaImpact(this.scene, kind, {
      ...opts,
      offsetX: this.ox,
      offsetY: this.oy,
    });
  }

  create(state: RaidBattleState): void {
    this.drawStageFloor();
    this.gestureGfx = this.scene.add
      .graphics()
      .setDepth(RaidDepth.gesture)
      .setPosition(this.ox, this.oy);
    this.overlay = this.scene.add
      .graphics()
      .setDepth(RaidDepth.telegraph)
      .setPosition(this.ox, this.oy);
    this.pinGfx = this.scene.add
      .graphics()
      .setDepth(RaidDepth.gesturePin)
      .setPosition(this.ox, this.oy);
    this.aggroGfx = this.scene.add
      .graphics()
      .setDepth(RaidDepth.aggroArrow)
      .setPosition(this.ox, this.oy);
    this.createBoss(state);
    for (const u of state.units) this.ensureUnit(state, u);
  }

  setGestureVisual(v: GestureVisual): void {
    this.gestureVisual = v;
  }

  /**
   * Hide this lane's display list from another camera (Sim Lab A|B).
   * Foot dust is parented under unit roots so it rides along.
   */
  ignoreFromCamera(cam: Phaser.Cameras.Scene2D.Camera): void {
    if (this.stageFloor) cam.ignore(this.stageFloor);
    if (this.playMaskGfx) cam.ignore(this.playMaskGfx);
    if (this.overlay) cam.ignore(this.overlay);
    if (this.gestureGfx) cam.ignore(this.gestureGfx);
    if (this.pinGfx) cam.ignore(this.pinGfx);
    if (this.aggroGfx) cam.ignore(this.aggroGfx);
    if (this.bossRoot) cam.ignore(this.bossRoot);
    for (const v of this.views.values()) {
      cam.ignore(v.root);
      cam.ignore(v.chrome);
    }
  }

  destroy(): void {
    this.disposed = true;
    for (const v of this.views.values()) {
      v.groundShadow.destroy();
      v.rig.destroy();
      v.root.destroy(true);
      v.chrome.destroy(true);
    }
    this.views.clear();
    this.prevAttacking.clear();
    this.prevKnockActive.clear();
    for (const b of this.speechBubbles.values()) b.destroy();
    this.speechBubbles.clear();
    this.unitFacing.clear();
    this.bossFacing = createDisplayFacingState();
    this.bossDying = false;
    this.bossDeathAnimTime = 0;
    this.bossGroundShadow?.destroy();
    this.bossGroundShadow = null;
    this.bossLandSquashPending = false;
    this.bossRig?.destroy();
    this.bossRoot?.destroy(true);
    this.overlay?.destroy();
    this.gestureGfx?.destroy();
    this.pinGfx?.destroy();
    this.aggroGfx?.destroy();
    this.playMaskGfx?.destroy();
    this.stageFloor?.destroy();
  }

  private drawStageFloor(): void {
    if (this.stage) {
      this.drawFlatStagePad(this.stage);
      return;
    }
    const map = raidCatalog.balance.map;
    const mapW = Number(map.width) || 540;
    const mapH = Number(map.height) || 960;
    const ground = (
      raidCatalog.view as {
        stageGround?: { textureKey?: string; path?: string };
      }
    ).stageGround;
    const key = ground?.textureKey ?? "raid:stage-ground";

    if (this.scene.textures.exists(key)) {
      const img = this.scene.add
        .image(this.ox, this.oy, key)
        .setOrigin(0, 0)
        .setDisplaySize(mapW, mapH)
        .setDepth(RaidDepth.ground);
      const tex = this.scene.textures.get(key);
      tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
      this.stageFloor = img;
    } else {
      // Fallback if preload missed the plate (tests / missing public asset).
      const b = map.playBounds ?? {
        xMin: 56,
        xMax: 484,
        yMin: 130,
        yMax: 860,
      };
      const gfx = this.scene.add
        .graphics()
        .setDepth(RaidDepth.ground)
        .setPosition(this.ox, this.oy);
      gfx.fillStyle(0x1a1c20, 0.92);
      gfx.fillRect(0, 0, mapW, mapH);
      gfx.fillStyle(0x2a2e34, 0.85);
      gfx.fillRect(b.xMin, b.yMin, b.xMax - b.xMin, b.yMax - b.yMin);
      gfx.lineStyle(2, 0x3a4048, 0.55);
      gfx.strokeRect(b.xMin, b.yMin, b.xMax - b.xMin, b.yMax - b.yMin);
      const step = 48;
      gfx.lineStyle(1, 0x32363c, 0.28);
      for (let x = b.xMin + step; x < b.xMax; x += step) {
        gfx.lineBetween(x, b.yMin, x, b.yMax);
      }
      for (let y = b.yMin + step; y < b.yMax; y += step) {
        gfx.lineBetween(b.xMin, y, b.xMax, y);
      }
      this.stageFloor = gfx;
    }

    this.drawPlayBoundsVignette(mapW, mapH);
  }

  /** Sim Lab flat pad — no battle stage-ground texture. */
  private drawFlatStagePad(stage: SimStageDef): void {
    const b = stage.playBounds;
    const gfx = this.scene.add
      .graphics()
      .setDepth(RaidDepth.ground)
      .setPosition(this.ox, this.oy);
    gfx.fillStyle(hexToColor(stage.ground.color), 1);
    gfx.fillRect(0, 0, stage.width, stage.height);
    gfx.fillStyle(0x2a2e34, 0.88);
    gfx.fillRect(b.xMin, b.yMin, b.xMax - b.xMin, b.yMax - b.yMin);
    gfx.lineStyle(2, 0x4a5560, 0.65);
    gfx.strokeRect(b.xMin, b.yMin, b.xMax - b.xMin, b.yMax - b.yMin);
    const step = 40;
    gfx.lineStyle(1, 0x32363c, 0.3);
    for (let x = b.xMin + step; x < b.xMax; x += step) {
      gfx.lineBetween(x, b.yMin, x, b.yMax);
    }
    for (let y = b.yMin + step; y < b.yMax; y += step) {
      gfx.lineBetween(b.xMin, y, b.xMax, y);
    }
    this.stageFloor = gfx;
    this.drawPlayBoundsVignette(stage.width, stage.height, b);
  }

  /** Darken side pillars / top relief / bottom ledge outside playBounds. */
  private drawPlayBoundsVignette(
    mapW: number,
    mapH: number,
    bounds?: { xMin: number; xMax: number; yMin: number; yMax: number },
  ): void {
    const map = raidCatalog.balance.map;
    const b = bounds ??
      map.playBounds ?? {
        xMin: 56,
        xMax: 484,
        yMin: 130,
        yMax: 860,
      };
    const vg = (
      raidCatalog.view as {
        playBoundsVignette?: {
          color?: string;
          sideAlpha?: number;
          edgeAlpha?: number;
        };
      }
    ).playBoundsVignette;
    const color = hexToColor(vg?.color ?? "#07080c");
    const sideA = Math.max(0, Math.min(1, Number(vg?.sideAlpha) || 0.42));
    const edgeA = Math.max(0, Math.min(1, Number(vg?.edgeAlpha) || 0.28));
    const gfx = this.scene.add
      .graphics()
      .setDepth(RaidDepth.playMask)
      .setPosition(this.ox, this.oy);
    // Left / right gutters (primary: non-tile pillars).
    if (b.xMin > 0) {
      gfx.fillStyle(color, sideA);
      gfx.fillRect(0, 0, b.xMin, mapH);
    }
    if (b.xMax < mapW) {
      gfx.fillStyle(color, sideA);
      gfx.fillRect(b.xMax, 0, mapW - b.xMax, mapH);
    }
    // Top relief + bottom ledge (lighter so face/mana band still read).
    if (b.yMin > 0) {
      gfx.fillStyle(color, edgeA);
      gfx.fillRect(b.xMin, 0, b.xMax - b.xMin, b.yMin);
    }
    if (b.yMax < mapH) {
      gfx.fillStyle(color, edgeA);
      gfx.fillRect(b.xMin, b.yMax, b.xMax - b.xMin, mapH - b.yMax);
    }
    this.playMaskGfx = gfx;
  }

  sync(state: RaidBattleState, events: readonly RaidSimEvent[]): void {
    if (this.disposed) return;
    this.hitBudget = 24;
    for (const ev of events) {
      if (ev.kind === RaidEvent.Spawn) {
        const u = state.units.find((x) => x.id === ev.unitId);
        if (u) {
          this.ensureUnit(state, u);
          // Thralls: ground pop. Chargers: amber appear. Brutes: drop entrance only.
          if (u.team === 1 && u.defId === "add") {
            this.playAddSpawnCue(u);
          } else if (u.team === 1 && u.defId === "charger") {
            this.playChargerSpawnCue(u);
          }
        }
      } else if (ev.kind === RaidEvent.Hit) {
        this.playHit(state, ev);
      } else if (ev.kind === RaidEvent.Heal) {
        this.playHeal(state, ev);
      } else if (ev.kind === RaidEvent.Death) {
        // Corpse knock (charger lethal): hold death FX until flight ends.
        const dead = state.units.find((x) => x.id === ev.unitId);
        if (!dead?.knock) this.beginUnitDeath(ev.unitId);
        this.clearSpeechBubble(ev.unitId);
      } else if (ev.kind === RaidEvent.Bark) {
        if (ev.speaker === "boss") {
          this.showBossBark(ev.messageKey);
        } else {
          this.showUnitBark(ev.unitId, ev.messageKey);
        }
      } else if (ev.kind === RaidEvent.LeapLand) {
        const leapFx = skillPresentation("leap");
        this.skillImpact( "circle", {
          x: ev.x,
          y: ev.y,
          radius: ev.radius,
          theme: "leap",
          skill: leapFx,
          phase: "impact",
        });
        const landFx = leapFx.impactVfx ?? "raid.leap.land";
        this.playVfx(landFx, {
          x: ev.x,
          y: ev.y,
          angle: 0,
          vars: { radius: ev.radius },
          ...VFX_LAYER,
        });
        this.lastLeapTravelT = -1;
        this.bossLandSquashPending = true;
      } else if (ev.kind === RaidEvent.LeapTelegraph) {
        if (ev.remain >= ev.duration - 0.05) {
          const leapFx = skillPresentation("leap");
          this.skillImpact( "circle", {
            x: ev.x,
            y: ev.y,
            radius: ev.radius,
            theme: "leap",
            skill: leapFx,
            phase: "open",
          });
          const openFx = leapFx.openVfx ?? "raid.leap.open";
          this.playVfx(openFx, {
            x: ev.x,
            y: ev.y,
            angle: 0,
            vars: { radius: ev.radius },
            ...VFX_LAYER,
          });
        }
      } else if (ev.kind === RaidEvent.Telegraph) {
        if (ev.remain >= ev.duration - 0.05) {
          const skill = skillPresentation(ev.skillId);
          if (ev.shape === "cone") {
            this.skillImpact( "cone", {
              x: ev.x,
              y: ev.y,
              range: ev.range,
              coneDeg: ev.coneDeg ?? 70,
              aimAngle: ev.aimAngle ?? 0,
              theme: aoeThemeFor(ev.skillId),
              skill,
              phase: "open",
            });
          } else if (ev.shape === "circle") {
            this.skillImpact( "circle", {
              x: ev.x,
              y: ev.y,
              radius: ev.range,
              theme: aoeThemeFor(ev.skillId),
              skill,
              phase: "open",
            });
          } else if (ev.shape === "line") {
            // Line-fill overlay is the telegraph cue — skip cone/circle open flash.
          } else {
            const _exhaustive: never = ev.shape;
            void _exhaustive;
          }
          if (ev.shape !== "line") {
            const openFx = skill.openVfx ?? "raid.slam.open";
            this.playVfx(openFx, {
              x: ev.x,
              y: ev.y,
              angle: ev.aimAngle ?? 0,
              vars: {
                range: ev.range,
                coneDeg: ev.coneDeg ?? 360,
              },
              ...VFX_LAYER,
            });
          }
        }
      } else if (ev.kind === RaidEvent.BrassResolve) {
        const skill = skillPresentation(ev.skillId);
        if (ev.skillId === "brass") {
          this.skillImpact( "cone", {
            x: ev.x,
            y: ev.y,
            range: ev.range,
            coneDeg: ev.coneDeg,
            aimAngle: ev.aimAngle,
            theme: "brass",
            skill,
            phase: "impact",
          });
        } else {
          this.skillImpact( "circle", {
            x: ev.x,
            y: ev.y,
            radius: ev.range,
            theme: aoeThemeFor(ev.skillId),
            skill,
            phase: "impact",
          });
        }
        const impactFx =
          skill.impactVfx ??
          (ev.skillId === "brass" || ev.skillId === "charge"
            ? "raid.brass.impact"
            : "raid.slam.impact");
        this.playVfx(impactFx, {
          x: ev.x,
          y: ev.y,
          angle: ev.aimAngle,
          vars: {
            range: ev.range,
            coneDeg: ev.coneDeg,
          },
          ...VFX_LAYER,
        });
      } else if (ev.kind === RaidEvent.MeleeImpact) {
        const melee = demoBossDef().melee as BossSkillFx & {
          impactVfx?: string;
          areaFlash?: boolean;
          areaFlashMs?: number;
        };
        this.skillImpact("circle", {
          x: ev.x,
          y: ev.y,
          radius: ev.radius,
          theme: "slam",
          skill: melee,
          phase: "impact",
        });
        const impactFx = melee.impactVfx ?? "raid.melee.impact";
        this.playVfx(impactFx, {
          x: ev.x,
          y: ev.y,
          angle: 0,
          vars: { radius: ev.radius },
          ...VFX_LAYER,
        });
      } else if (ev.kind === RaidEvent.Break) {
        this.playVfx("raid.break", {
          x: ev.x,
          y: ev.y,
          angle: 0,
          ...VFX_LAYER,
        });
        this.playVfx("df.volume.pulse", {
          x: ev.x,
          y: ev.y,
          angle: 0,
          ...VFX_LAYER,
        });
        this.playVfx("df.volume.pulse", {
          x: ev.x,
          y: ev.y - 20,
          angle: 0,
          ...VFX_LAYER,
        });
        playFloatText(
          this.scene,
          this.wx(ev.x),
          this.wy(ev.y - 40),
          {
            template: "BREAK",
            color: "#d8d8e0",
            rise: 48,
            duration: 900,
            offsetY: -8,
            fontSize: 18,
            stroke: "#202028",
            strokeThickness: 4,
          },
          {},
          RaidDepth.float,
        );
      } else if (ev.kind === RaidEvent.Encounter) {
        const map = raidCatalog.balance.map;
        const cx = map.width * 0.5;
        const cy = map.height * 0.42;
        playFloatText(
          this.scene,
          this.wx(cx),
          this.wy(cy),
          {
            template: t(ev.messageKey),
            color: "#ffe6a8",
            rise: 36,
            duration: 1400,
            offsetY: -4,
            fontSize: 16,
            stroke: "#1a1410",
            strokeThickness: 4,
          },
          {},
          RaidDepth.float,
        );
      }
    }

    // Start death presentation once corpse knock (if any) has finished.
    for (const u of state.units) {
      if (u.hp <= 0 && !u.knock) this.beginUnitDeath(u.id);
    }
    if (
      (state.phase === "bossDying" || state.phase === "won") &&
      !this.bossDying
    ) {
      this.bossDying = true;
      this.bossDeathAnimTime = 0;
      this.playVfx("raid.bossDeath", {
        x: state.boss.x,
        y: state.boss.y,
        angle: 0,
        ...VFX_LAYER,
      });
    }

    let bossDrawY = state.boss.y;
    let bossDrawX = state.boss.x;
    const leap = state.boss.leap;
    const dropApex = 220;
    const enc = state.encounter;
    if (
      enc.mode === "transition" &&
      enc.transitionPhase === "drop"
    ) {
      const script = (
        raidCatalog.assault.scripts as Record<
          string,
          { transition: { dropSec: number } }
        >
      )[enc.scriptId];
      const dropSec = Math.max(0.05, script?.transition.dropSec ?? 1.1);
      const frac = Math.max(0, Math.min(1, 1 - enc.transitionRemain / dropSec));
      // Ease-in land from above.
      const rise = (1 - frac) * (1 - frac);
      bossDrawY = state.boss.y - dropApex * rise;
      bossDrawX = state.boss.x;
    } else if (leap && leap.phase === "travel") {
      const travelDur = Math.max(0.05, leap.duration);
      const tLin = 1 - Math.max(0, leap.remain) / travelDur;
      const t = Math.min(1, Math.max(0, tLin));
      const u = easeInOutCubic(t);
      const height = parabola01(t) * LEAP_APEX;
      // Prefer display XY from ease (sim already eases; keep in sync if remain jitter)
      bossDrawX = leap.fromX + (leap.landX - leap.fromX) * u;
      bossDrawY =
        leap.fromY + (leap.landY - leap.fromY) * u - height;
      const groundX = leap.fromX + (leap.landX - leap.fromX) * u;
      const groundY = leap.fromY + (leap.landY - leap.fromY) * u;
      const persp = sampleAirborneDepth(height, LEAP_APEX, {
        maxScaleBoost: LEAP_SCALE_BOOST,
        shadowMinScale: 0.48,
        shadowMinAlpha: 0.4,
      });
      this.bossRoot.setScale(this.bossBaseScale * persp.bodyScale);
      const tilt = airborneTiltRad(
        { x: leap.fromX, y: leap.fromY },
        { x: leap.landX, y: leap.landY },
        persp.lift01,
        LEAP_TILT_RAD,
      );
      this.bossRoot.setAngle((tilt * 180) / Math.PI);
      if (this.bossGroundShadow) {
        this.bossGroundShadow
          .setVisible(true)
          .setPosition(this.wx(groundX), this.wy(groundY))
          .setScale(persp.shadowScale)
          .setAlpha(0.28 * persp.shadowAlphaMul);
      }
      this.bossRig?.setShadowVisible(false);
      if (this.lastLeapTravelT < 0.45 && t >= 0.45) {
        this.playVfx("raid.leap.apex", {
          x: bossDrawX,
          y: bossDrawY,
          angle: 0,
          ...VFX_LAYER,
        });
      }
      this.lastLeapTravelT = t;
    } else {
      // Windup or idle — feet on ground (travel-only perspective).
      if (!leap) this.lastLeapTravelT = -1;
      this.bossRoot.setScale(this.bossBaseScale);
      this.bossRoot.setAngle(0);
      this.bossGroundShadow?.setVisible(false);
      this.bossRig?.setShadowVisible(true);
      if (!leap && this.bossLandSquashPending) {
        this.bossLandSquashPending = false;
        this.playBossLandSquash();
      }
    }
    this.bossRoot.setPosition(this.wx(bossDrawX), this.wy(bossDrawY));
    this.bossRoot.setDepth(raidBossDepth(state.boss.y, MAP_H));
    // Hide until drop starts or combat armed (dormant off-map / unarmed).
    const showBoss =
      !isBossDormant(state) ||
      (enc.mode === "transition" &&
        (enc.transitionPhase === "drop" || enc.transitionPhase === "hold"));
    this.bossRoot.setVisible(showBoss);
    this.trailBudget.left =
      Number(raidCatalog.view.rig?.trailBudgetPerFrame) || 8;
    const mapH = MAP_H;
    const dt = 1 / 30;
    {
      const bossArt = raidCatalog.view.bossArt;
      const aff = affiliationForBinaryTeam(1);
      const bossSwing = swingSecForBoss();
      const bdef = demoBossDef();
      const deathFx = raidDeathFx();
      let bossAnim: string;
      let bossAnimTime: number;
      let bossIsDying = this.bossDying;
      if (bossIsDying) {
        const holdTotal = deathFx.bossDeathSec;
        this.bossDeathAnimTime = Math.max(
          0,
          holdTotal - Math.max(0, state.resultHoldRemain),
        );
        // After won, keep scrubbing death clip for residual frames.
        if (state.phase === "won") {
          this.bossDeathAnimTime = Math.max(
            this.bossDeathAnimTime,
            holdTotal,
          );
        }
        bossAnim = "death";
        bossAnimTime = this.bossDeathAnimTime;
      } else {
        const swinging = state.boss.attackAnimT > 0;
        const telegraph = state.boss.telegraph;
        const leapWind = state.boss.leap;
        const spd = Math.hypot(state.boss.intentVx, state.boss.intentVy);
        // Melee AA → melee.anim; skill cast → castAnim (synced to windup); else walk/idle.
        if (swinging) {
          bossAnim = bdef.melee.anim ?? "attack";
          bossAnimTime = Math.max(0, bossSwing - state.boss.attackAnimT);
        } else if (telegraph) {
          const cast = skillPresentation(telegraph.skillId).castAnim ?? "idle";
          bossAnim = cast;
          bossAnimTime = Math.max(0, telegraph.duration - telegraph.remain);
        } else if (leapWind?.phase === "windup") {
          bossAnim = skillPresentation("leap").castAnim ?? "idle";
          bossAnimTime = Math.max(0, leapWind.duration - leapWind.remain);
        } else if (spd >= WALK_ENTER) {
          bossAnim = "walk";
          bossAnimTime = state.time;
        } else {
          bossAnim = "idle";
          bossAnimTime = state.time;
        }
      }
      const nearestAlly = state.units.find((a) => a.team === 0 && a.hp > 0);
      const leapAim =
        state.boss.leap != null
          ? Math.atan2(
              state.boss.leap.landY - state.boss.y,
              state.boss.leap.landX - state.boss.x,
            )
          : null;
      const spdLive = Math.hypot(state.boss.intentVx, state.boss.intentVy);
      const aimTarget =
        leapAim ??
        state.boss.telegraph?.aimAngle ??
        (spdLive >= WALK_ENTER
          ? Math.atan2(state.boss.intentVy, state.boss.intentVx)
          : nearestAlly
            ? Math.atan2(nearestAlly.y - state.boss.y, nearestAlly.x - state.boss.x)
            : Math.PI / 2);
      const stepped = stepDisplayFacing(
        this.bossFacing,
        aimTarget,
        DISPLAY_AIM_TURN_RATE,
        dt,
        FACING_STEP_OPTS,
      );
      this.bossFacing = stepped.state;
      const aim = stepped.aim;
      const facingDir = stepped.facing;
      const { key } = ArtPipeline.ensureAnimFrame(
        this.scene,
        bossArt.packId,
        bossArt.role,
        aff,
        bossAnim,
        bossAnimTime,
        facingDir,
      );
      if (this.bossBody.texture.key !== key) this.bossBody.setTexture(key);
      if (this.bossRig) {
        this.bossRig.update(
          {
            animState: bossIsDying
              ? "death"
              : bossAnim === "idle" || bossAnim === "walk"
                ? bossAnim
                : "attack",
            animTime: bossAnimTime,
            intentVx: bossIsDying ? 0 : state.boss.intentVx,
            intentVy: bossIsDying ? 0 : state.boss.intentVy,
            facing: Math.cos(aim) >= 0 ? 1 : -1,
            dying: bossIsDying,
            aimAngle: aim,
            facingDir,
            lockBodyFlip: true,
          },
          this.trailBudget,
          this.scene,
          { x: state.boss.x, y: state.boss.y },
        );
      }
      if (bossIsDying) {
        const fadeStart = deathFx.bossDeathSec * 0.55;
        const fadeT =
          this.bossDeathAnimTime <= fadeStart
            ? 1
            : Math.max(
                0,
                1 -
                  (this.bossDeathAnimTime - fadeStart) /
                    Math.max(0.05, deathFx.bossDeathSec - fadeStart),
              );
        this.bossBody.clearTint();
        this.bossBody.setAlpha(fadeT);
        this.bossRig?.container.setAlpha(fadeT);
      } else {
        // Toughness Break: hard blink while stun timer runs
        const broken = state.time < state.boss.stunnedUntil;
        if (broken) {
          const blinkOn = stunBlinkOn(state.time);
          const a = stunBlinkAlphas(blinkOn, "boss");
          this.bossBody.setTint(STUN_BLINK_TINT);
          this.bossBody.setAlpha(a.body);
          this.bossRig?.container.setAlpha(a.rig);
        } else {
          this.bossBody.clearTint();
          this.bossBody.setAlpha(1);
          this.bossRig?.container.setAlpha(1);
        }
      }
    }
    const escalateStacks = getEscalateStacks(state);
    for (const u of state.units) {
      const v = this.views.get(u.id);
      if (!v) continue;
      // Dying / dead: still follow corpse knock fling (charger/leap lethal).
      if (v.dying || u.hp <= 0) {
        if (v.escalateBadge) v.escalateBadge.setVisible(false);
        if (u.hp <= 0) v.chrome.setVisible(false);
        if (u.knock) {
          this.applyUnitKnockFlightVisual(v, u, dt, /*trail*/ true);
          this.prevKnockActive.set(u.id, true);
        } else {
          this.clearUnitKnockFlightVisual(v);
          v.root.setPosition(this.wx(u.x), this.wy(u.y));
          this.prevKnockActive.set(u.id, false);
        }
        continue;
      }
      v.root.setAlpha(1);
      v.chrome.setAlpha(1);
      const wasKnock = this.prevKnockActive.get(u.id) === true;
      const knocking = u.knock != null;
      this.prevKnockActive.set(u.id, knocking);
      let drawX = u.x;
      let drawY = u.y;
      // Brute entrance drop: fall from above onto spawn XY.
      if (u.defId === "brute" && !u.combatArmed && u.dropRemain > 0) {
        this.clearUnitKnockFlightVisual(v);
        const dropSec =
          Number(
            (
              raidCatalog.assault.scripts as Record<
                string,
                { bruteDropSec?: number }
              >
            )[state.encounter.scriptId]?.bruteDropSec,
          ) || 1.1;
        const frac = Math.max(0, Math.min(1, 1 - u.dropRemain / dropSec));
        const rise = (1 - frac) * (1 - frac);
        drawY = u.y - 180 * rise;
        v.root.setPosition(this.wx(drawX), this.wy(drawY));
        v.chrome.setPosition(this.wx(drawX), this.wy(drawY));
      } else if (u.knock) {
        const flight = this.applyUnitKnockFlightVisual(v, u, dt, true);
        drawX = flight.bodyX;
        drawY = flight.bodyY;
        v.chrome.setPosition(this.wx(drawX), this.wy(drawY));
      } else {
        this.clearUnitKnockFlightVisual(v);
        if (wasKnock) {
          drawX = u.x;
          drawY = u.y;
          v.root.setPosition(this.wx(drawX), this.wy(drawY));
          v.chrome.setPosition(this.wx(drawX), this.wy(drawY));
          this.playLandSquash(v);
          this.playVfx("raid.knock.land", {
            x: u.x,
            y: u.y,
            angle: 0,
            ...VFX_LAYER,
          });
        } else {
          const pos = stepDisplayPos(
            v.root.x - this.ox,
            v.root.y - this.oy,
            u.x,
            u.y,
            FOLLOW_HZ,
            FOLLOW_SNAP,
            dt,
          );
          drawX = pos.x;
          drawY = pos.y;
          v.root.setPosition(this.wx(drawX), this.wy(drawY));
          v.chrome.setPosition(this.wx(drawX), this.wy(drawY));
        }
      }
      const groundX = knocking && u.knock ? u.x : drawX;
      const groundY = knocking && u.knock ? u.y : drawY;
      const aimTarget = { x: u.aimX, y: u.aimY };
      const aimRaw = Math.atan2(aimTarget.y - u.y, aimTarget.x - u.x);
      const facingState =
        this.unitFacing.get(u.id) ?? createDisplayFacingState();
      const stepped = stepDisplayFacing(
        facingState,
        aimRaw,
        DISPLAY_AIM_TURN_RATE,
        dt,
        FACING_STEP_OPTS,
      );
      this.unitFacing.set(u.id, stepped.state);
      const aim = stepped.aim;
      const facingDir = stepped.facing;
      const moveFacing: 1 | -1 =
        Math.abs(u.intentVx) > 8
          ? u.intentVx >= 0
            ? 1
            : -1
          : Math.cos(aim) >= 0
            ? 1
            : -1;
      const stunned = state.time < u.stunnedUntil;
      const spd = stunned ? 0 : Math.hypot(u.intentVx, u.intentVy);
      const swingSec = swingSecForClass(u.classId);
      const swinging = !stunned && u.attackAnimT > 0;
      const wasAttacking = this.prevAttacking.get(u.id) === true;
      const risingAttack = swinging && !wasAttacking;
      this.prevAttacking.set(u.id, swinging);
      if (!swinging && wasAttacking) {
        v.rig.restoreAttackStyle();
      }
      const anim = swinging
        ? "attack"
        : stunned || knocking
          ? "idle"
          : spd >= WALK_ENTER
            ? "walk"
            : "idle";
      const animTime = swinging
        ? risingAttack
          ? 0
          : Math.max(0, swingSec - u.attackAnimT)
        : state.time;
      const aff = v.affiliationKey;
      const { key } = ArtPipeline.ensureAnimFrame(
        this.scene,
        "raid",
        v.animRole,
        aff,
        anim,
        animTime,
        facingDir,
      );
      if (risingAttack || v.body.texture.key !== key) {
        v.body.setTexture(key);
      }
      if (risingAttack && u.team === 0 && v.role === "raid_dps") {
        const muzzle = v.rig.muzzleWorld({ x: groundX, y: groundY });
        this.playVfx("df.origin.muzzle", {
          x: muzzle.x,
          y: muzzle.y,
          angle: aim,
          ...VFX_LAYER,
        });
      }
      v.rig.update(
        {
          animState: anim,
          animTime,
          intentVx: u.intentVx,
          intentVy: u.intentVy,
          facing: moveFacing,
          dying: false,
          aimAngle: aim,
          facingDir,
          lockBodyFlip: true,
        },
        this.trailBudget,
        this.scene,
        { x: groundX, y: groundY },
      );
      // UnitRig.update re-shows embedded shadow — keep floor ellipse only while airborne.
      if (knocking) v.rig.setShadowVisible(false);
      // Ally AoE stun: same blink language as boss break (softer alphas).
      if (u.team === 0 && stunned) {
        const blinkOn = stunBlinkOn(state.time);
        const a = stunBlinkAlphas(blinkOn, "ally");
        v.body.setTint(STUN_BLINK_TINT);
        v.body.setAlpha(a.body);
        v.rig.container.setAlpha(a.rig);
      } else {
        v.body.clearTint();
        v.body.setAlpha(1);
        v.rig.container.setAlpha(1);
      }
      const damaged = u.hp < u.maxHp;
      const showHp = damaged || (u.team === 0 && stunned);
      const base = damaged || stunned ? RaidDepth.unitActive : RaidDepth.unitIdle;
      v.root.setDepth(raidBodyDepth(base, u.y, mapH));
      updateHpBar(
        { bg: v.hpBg, ghost: v.hpGhost, fill: v.hpFill },
        {
          ratio: u.hp / u.maxHp,
          visible: showHp,
          barW: v.barW,
        },
      );
      if (v.skillBg && v.skillFill && u.team === 0 && u.skillCdMax > 0) {
        const ready =
          u.skillCdMax > 0
            ? 1 - Math.max(0, Math.min(1, u.skillCdLeft / u.skillCdMax))
            : 1;
        const skH = Math.max(2, v.hpBg.displayHeight * 0.55);
        v.skillBg.setDisplaySize(v.barW, skH);
        v.skillFill.setDisplaySize(v.barW * ready, skH);
        v.skillFill.setPosition(-v.barW / 2, v.skillBg.y);
        const readyCol = hexToColor(
          (raidCatalog.view.colors as { skillCdBarReady?: string })
            .skillCdBarReady ?? "#7dffb0",
        );
        const chargeCol = hexToColor(
          (raidCatalog.view.colors as { skillCdBar?: string }).skillCdBar ??
            "#ffe08a",
        );
        v.skillFill.setFillStyle(ready >= 0.999 ? readyCol : chargeCol);
        v.skillBg.setVisible(true);
        v.skillFill.setVisible(true);
      } else if (v.skillBg && v.skillFill) {
        v.skillBg.setVisible(false);
        v.skillFill.setVisible(false);
      }
      if (v.stunIcon) {
        const skH = v.skillBg
          ? Math.max(2, v.hpBg.displayHeight * 0.55)
          : 2;
        const pos = allyStunIconLocalPos({
          barW: v.barW,
          hpY: v.hpY,
          barH: Math.max(2, v.hpBg.displayHeight),
          skillY: v.skillBg?.visible ? v.skillBg.y : null,
          skillH: skH,
        });
        v.stunIcon.setPosition(pos.x, pos.y);
        v.stunIcon.setVisible(u.team === 0 && stunned);
      }
      if (v.escalateBadge) {
        const barH = Math.max(2, v.hpBg.displayHeight);
        const pos = enemyEscalateBadgeLocalPos({ hpY: v.hpY, barH });
        v.escalateBadge.setPosition(pos.x, pos.y);
        const showEsc = shouldShowEnemyEscalateBadge({
          team: u.team,
          escalateStacks,
          floatHpVisible: showHp,
          alive: u.hp > 0 && !v.dying,
        });
        if (showEsc) {
          v.escalateBadge.setText(enemyEscalateBadgeText(escalateStacks));
        }
        v.escalateBadge.setVisible(showEsc);
      }
    }
    this.stepDyingViews(dt, mapH);
    this.drawTelegraph(state);
    this.drawGestureLayer(state);
    this.drawAggroArrow(state);
  }

  private beginUnitDeath(id: number): void {
    const v = this.views.get(id);
    if (!v || v.dying) return;
    v.dying = true;
    v.deathAnimTime = 0;
    v.deathDuration = raidDeathFx().unitDeathSec;
    v.chrome.setVisible(false);
    this.prevAttacking.set(id, false);
    this.clearSpeechBubble(id);
    this.playVfx("raid.death", {
      x: this.toLocal(v.root).x,
      y: this.toLocal(v.root).y,
      angle: 0,
      ...VFX_LAYER,
    });
  }

  private clearSpeechBubble(unitId: number): void {
    const prev = this.speechBubbles.get(unitId);
    if (!prev) return;
    prev.destroy();
    this.speechBubbles.delete(unitId);
  }

  private speechBubbleTable(): {
    offsetY?: number;
    bossOffsetY?: number;
    durationMs?: number;
    fontSize?: number;
    maxWidth?: number;
    padX?: number;
    padY?: number;
    tailH?: number;
    bg?: string;
    border?: string;
    text?: string;
    ally?: { bg?: string; border?: string; text?: string };
    enemy?: { bg?: string; border?: string; text?: string };
  } {
    return (
      (
        raidCatalog.view as {
          speechBubble?: {
            offsetY?: number;
            bossOffsetY?: number;
            durationMs?: number;
            fontSize?: number;
            maxWidth?: number;
            padX?: number;
            padY?: number;
            tailH?: number;
            bg?: string;
            border?: string;
            text?: string;
            ally?: { bg?: string; border?: string; text?: string };
            enemy?: { bg?: string; border?: string; text?: string };
          };
        }
      ).speechBubble ?? {}
    );
  }

  private barkStyle(enemy: boolean, boss = false): Partial<SpeechBubbleStyle> {
    const sb = this.speechBubbleTable();
    const faction = enemy ? sb.enemy : sb.ally;
    return {
      offsetY: boss
        ? (sb.bossOffsetY ?? -78)
        : (sb.offsetY ?? DEFAULT_SPEECH_BUBBLE.offsetY),
      durationMs: sb.durationMs,
      fontSize: sb.fontSize,
      maxWidth: sb.maxWidth,
      padX: sb.padX,
      padY: sb.padY,
      tailH: sb.tailH,
      bg: faction?.bg ?? sb.bg ?? (enemy ? "#2a1218" : "#1c1c28"),
      border: faction?.border ?? sb.border ?? (enemy ? "#ff5a7a" : "#e8dcc8"),
      text: faction?.text ?? sb.text ?? (enemy ? "#ffb0b8" : "#f4efe6"),
    };
  }

  private showBossBark(messageKey: string): void {
    if (!this.bossRoot) return;
    const id = BOSS_SPEECH_UNIT_ID;
    this.clearSpeechBubble(id);
    const handle = playSpeechBubble(
      this.scene,
      this.bossRoot,
      id,
      t(messageKey),
      this.barkStyle(true, true),
    );
    this.speechBubbles.set(id, handle);
    handle.root.once("destroy", () => {
      if (this.speechBubbles.get(id) === handle) {
        this.speechBubbles.delete(id);
      }
    });
  }

  private showUnitBark(unitId: number, messageKey: string): void {
    const v = this.views.get(unitId);
    if (!v || v.dying) return;
    this.clearSpeechBubble(unitId);
    const handle = playSpeechBubble(
      this.scene,
      v.chrome,
      unitId,
      t(messageKey),
      this.barkStyle(v.team === 1, false),
    );
    this.speechBubbles.set(unitId, handle);
    // Drop map entry when tween destroys the container.
    handle.root.once("destroy", () => {
      if (this.speechBubbles.get(unitId) === handle) {
        this.speechBubbles.delete(unitId);
      }
    });
  }

  private stepDyingViews(dt: number, mapH: number): void {
    const purge: number[] = [];
    for (const [id, v] of this.views) {
      if (!v.dying) continue;
      v.deathAnimTime += dt;
      const aff = v.affiliationKey;
      const dir = this.unitFacing.get(id)?.facing ?? "s";
      const { key } = ArtPipeline.ensureAnimFrame(
        this.scene,
        "raid",
        v.role,
        aff,
        "death",
        v.deathAnimTime,
        dir,
      );
      if (v.body.texture.key !== key) v.body.setTexture(key);
      v.rig.update(
        {
          animState: "death",
          animTime: v.deathAnimTime,
          intentVx: 0,
          intentVy: 0,
          facing: 1,
          dying: true,
          aimAngle: Math.PI / 2,
          facingDir: dir,
          lockBodyFlip: true,
        },
        this.trailBudget,
        this.scene,
        { x: v.root.x, y: v.root.y },
      );
      const fadeStart = v.deathDuration * 0.5;
      const alpha =
        v.deathAnimTime <= fadeStart
          ? 1
          : Math.max(
              0,
              1 -
                (v.deathAnimTime - fadeStart) /
                  Math.max(0.05, v.deathDuration - fadeStart),
            );
      v.root.setAlpha(alpha);
      v.root.setDepth(raidBodyDepth(RaidDepth.unitIdle, v.root.y, mapH));
      if (v.deathAnimTime >= v.deathDuration) purge.push(id);
    }
    for (const id of purge) this.destroyView(id);
  }

  private createBoss(state: RaidBattleState): void {
    const bossArt = raidCatalog.view.bossArt;
    const scale = raidCatalog.view.display.bossScale;
    const baseSize = 56 * scale;
    const aff = affiliationForBinaryTeam(1);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      bossArt.packId,
      bossArt.role,
      aff,
      "idle",
      0,
      "s",
    );
    this.bossBody = this.scene.add
      .image(0, 0, key)
      .setDisplaySize(baseSize, baseSize);
    this.bossRig = new UnitRig(
      this.scene,
      this.bossBody,
      bossArt.role,
      RIG,
      WALK_ENTER,
      undefined,
      raidGripPack,
    );
    this.bossBaseScale = 1;
    this.bossRoot = this.scene.add
      .container(this.wx(state.boss.x), this.wy(state.boss.y), [
        this.bossRig.container,
      ])
      .setDepth(RaidDepth.boss)
      .setScale(this.bossBaseScale);
    const bossSh = (
      (RIG.byRole as Record<string, { shadowRx?: number; shadowRy?: number }>)
        .raid_boss ?? RIG.defaults
    );
    this.bossGroundShadow = this.scene.add
      .ellipse(
        this.wx(state.boss.x),
        this.wy(state.boss.y),
        (bossSh.shadowRx ?? 14) * 2.4,
        (bossSh.shadowRy ?? 5) * 2.4,
        0x000000,
        0.28,
      )
      .setVisible(false)
      .setDepth(RaidDepth.boss - 1);
  }

  private destroyView(id: number): void {
    const v = this.views.get(id);
    if (!v) return;
    v.groundShadow.destroy();
    v.rig.destroy();
    v.root.destroy(true);
    v.chrome.destroy(true);
    this.views.delete(id);
    this.prevAttacking.delete(id);
    this.prevKnockActive.delete(id);
    this.unitFacing.delete(id);
  }

  /**
   * Perspective knock flight: body lifts + scale-up toward camera + tilt,
   * soft ground shadow stays on floor, optional debris trail.
   */
  private applyUnitKnockFlightVisual(
    v: UnitView,
    u: RaidUnit,
    dt: number,
    trail: boolean,
  ): { bodyX: number; bodyY: number; groundX: number; groundY: number } {
    const k = u.knock!;
    const s = sampleKnockFlight(
      k.t / k.dur,
      { x: k.fromX, y: k.fromY },
      { x: k.toX, y: k.toY },
      k.apex,
    );
    const bodyX = s.x;
    const bodyY = s.y - s.height;
    const groundX = s.x;
    const groundY = s.y;
    const persp = sampleAirborneDepth(s.height, Math.max(1, k.apex), {
      maxScaleBoost: KNOCK_SCALE_BOOST,
      shadowMinScale: 0.4,
      shadowMinAlpha: 0.35,
    });
    const tilt = airborneTiltRad(
      { x: k.fromX, y: k.fromY },
      { x: k.toX, y: k.toY },
      persp.lift01,
      KNOCK_TILT_RAD,
    );
    v.root.setPosition(this.wx(bodyX), this.wy(bodyY));
    v.rig.container.setScale(persp.bodyScale);
    v.rig.container.setAngle((tilt * 180) / Math.PI);
    v.rig.setShadowVisible(false);
    v.groundShadow
      .setVisible(true)
      .setPosition(this.wx(groundX), this.wy(groundY))
      .setScale(persp.shadowScale)
      .setAlpha(0.26 * persp.shadowAlphaMul)
      .setDepth(raidBodyDepth(RaidDepth.unitIdle, groundY, MAP_H) - 1);
    if (trail) {
      v.knockTrailAcc += dt;
      if (v.knockTrailAcc >= KNOCK_TRAIL_SEC) {
        v.knockTrailAcc = 0;
        this.playVfx("raid.knock.trail", {
          x: bodyX,
          y: bodyY,
          angle: Math.atan2(k.toY - k.fromY, k.toX - k.fromX),
          ...VFX_LAYER,
        });
      }
    }
    return { bodyX, bodyY, groundX, groundY };
  }

  private clearUnitKnockFlightVisual(v: UnitView): void {
    v.rig.container.setScale(1);
    v.rig.container.setAngle(0);
    v.rig.setShadowVisible(true);
    v.groundShadow.setVisible(false);
    v.knockTrailAcc = 0;
  }

  private playBossLandSquash(): void {
    const body = this.bossBody;
    if (!body) return;
    this.scene.tweens.killTweensOf(body);
    const sx = body.scaleX;
    const sy = body.scaleY;
    body.setScale(sx * 1.22, sy * 0.68);
    this.scene.tweens.add({
      targets: body,
      scaleX: sx,
      scaleY: sy,
      duration: 110,
      ease: "Cubic.easeOut",
    });
  }

  private playLandSquash(v: UnitView): void {
    const body = v.body;
    this.scene.tweens.killTweensOf(body);
    const sx = body.scaleX;
    const sy = body.scaleY;
    body.setScale(sx * 1.18, sy * 0.72);
    this.scene.tweens.add({
      targets: body,
      scaleX: sx,
      scaleY: sy,
      duration: 90,
      ease: "Cubic.easeOut",
    });
  }

  /** Brief ground ring + scale pop-in for team-1 thrall spawns (north / ring / periodic). */
  private playAddSpawnCue(u: RaidUnit): void {
    const cfg = (
      raidCatalog.view as {
        addSpawnFx?: { preset?: string; popFrom?: number; popMs?: number };
      }
    ).addSpawnFx;
    const preset = cfg?.preset ?? "raid.spawn";
    this.playVfx(preset, {
      x: u.x,
      y: u.y,
      angle: 0,
      ...VFX_LAYER,
    });
    const v = this.views.get(u.id);
    if (!v) return;
    const body = v.body;
    this.scene.tweens.killTweensOf(body);
    const sx = body.scaleX;
    const sy = body.scaleY;
    const from = Math.max(0.12, Math.min(0.85, Number(cfg?.popFrom) || 0.32));
    const ms = Math.max(80, Number(cfg?.popMs) || 240);
    body.setScale(sx * from, sy * from);
    this.scene.tweens.add({
      targets: body,
      scaleX: sx,
      scaleY: sy,
      duration: ms,
      ease: "Back.easeOut",
    });
  }

  /** Amber spider appear cue — distinct from thrall raid.spawn. */
  private playChargerSpawnCue(u: RaidUnit): void {
    const cfg = (
      raidCatalog.view as {
        chargerSpawnFx?: { preset?: string; popFrom?: number; popMs?: number };
      }
    ).chargerSpawnFx;
    const preset = cfg?.preset ?? "raid.charger.spawn";
    this.playVfx(preset, {
      x: u.x,
      y: u.y,
      angle: 0,
      ...VFX_LAYER,
    });
    const v = this.views.get(u.id);
    if (!v) return;
    const body = v.body;
    this.scene.tweens.killTweensOf(body);
    const sx = body.scaleX;
    const sy = body.scaleY;
    const from = Math.max(0.12, Math.min(0.85, Number(cfg?.popFrom) || 0.28));
    const ms = Math.max(80, Number(cfg?.popMs) || 280);
    body.setScale(sx * from, sy * from);
    this.scene.tweens.add({
      targets: body,
      scaleX: sx,
      scaleY: sy,
      duration: ms,
      ease: "Back.easeOut",
    });
  }

  private artForUnit(u: RaidUnit): {
    packId: string;
    role: string;
    displayScale?: number;
  } {
    if (u.defId === "brute") {
      const ba = (
        raidCatalog.view as {
          bruteArt?: { packId: string; role: string };
        }
      ).bruteArt;
      return {
        packId: ba?.packId ?? "raid",
        role: ba?.role ?? "raid_brute",
      };
    }
    if (u.defId === "charger") {
      const ca = (
        raidCatalog.view as {
          chargerArt?: { packId: string; role: string; displayScale?: number };
        }
      ).chargerArt;
      return {
        packId: ca?.packId ?? "raid",
        role: ca?.role ?? "raid_charger",
        displayScale: ca?.displayScale,
      };
    }
    if (u.team === 1) return raidCatalog.view.addArt;
    const map = raidCatalog.view.classArt as Record<
      string,
      { packId: string; role: string }
    >;
    return map[u.classId] ?? map.dps!;
  }

  private ensureUnit(state: RaidBattleState, u: RaidUnit): void {
    if (this.views.has(u.id)) return;
    const art = this.artForUnit(u);
    const look =
      u.defId === "brute"
        ? bruteVariantLookForUnit(state.units, u.id)
        : null;
    const aff = look?.affiliationKey ?? affiliationForBinaryTeam(u.team);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      art.packId,
      art.role,
      aff,
      "idle",
      0,
      "s",
    );
    const bossScale = raidCatalog.view.display.bossScale;
    const roleScale =
      Number.isFinite(art.displayScale) && (art.displayScale as number) > 0
        ? (art.displayScale as number)
        : 1;
    const baseSize =
      u.defId === "brute"
        ? 56 * bossScale * 0.5
        : raidCatalog.view.display.unitBaseSize *
          raidCatalog.view.display.unitScale *
          roleScale;
    const barH = 4;
    const barW = baseSize * 0.9;
    const hpY = -baseSize * 0.55;
    const skillH = 2;
    const skillY = hpY + barH * 0.5 + skillH * 0.5 + 1;
    const body = this.scene.add
      .image(0, 0, key)
      .setDisplaySize(baseSize, baseSize);
    const rig = new UnitRig(
      this.scene,
      body,
      art.role,
      RIG,
      WALK_ENTER,
      look?.bladeColor,
      raidGripPack,
    );
    const fillHex =
      u.team === 0
        ? raidCatalog.view.colors.hpBarAlly
        : raidCatalog.view.colors.hpBarEnemy;
    const { bg: hpBg, ghost: hpGhost, fill: hpFill } = createHpBar(this.scene, {
      barW,
      barH,
      y: hpY,
      bgColor: hexToColor(raidCatalog.view.colors.hpBarBg),
      fillColor: hexToColor(fillHex),
      bgAlpha: 0.75,
    });
    let skillBg: Phaser.GameObjects.Rectangle | null = null;
    let skillFill: Phaser.GameObjects.Rectangle | null = null;
    let stunIcon: Phaser.GameObjects.Text | null = null;
    let escalateBadge: Phaser.GameObjects.Text | null = null;
    const chromeKids: Phaser.GameObjects.GameObject[] = [hpBg, hpGhost, hpFill];
    if (u.team === 0 && u.skillCdMax > 0) {
      skillBg = this.scene.add
        .rectangle(0, skillY, barW, skillH, hexToColor(raidCatalog.view.colors.hpBarBg), 0.75)
        .setOrigin(0.5);
      skillFill = this.scene.add
        .rectangle(
          -barW / 2,
          skillY,
          barW,
          skillH,
          hexToColor(
            (raidCatalog.view.colors as { skillCdBarReady?: string })
              .skillCdBarReady ?? "#7dffb0",
          ),
        )
        .setOrigin(0, 0.5);
      chromeKids.push(skillBg, skillFill);
    }
    if (u.team === 0) {
      const iconPos = allyStunIconLocalPos({
        barW,
        hpY,
        barH,
        skillY: skillBg ? skillY : null,
        skillH,
      });
      stunIcon = this.scene.add
        .text(iconPos.x, iconPos.y, ALLY_STUN_ICON_GLYPH, {
          fontFamily: "Segoe UI Symbol, Apple Color Emoji, sans-serif",
          fontSize: "11px",
          color: "#e8e0ff",
          stroke: "#1a1420",
          strokeThickness: 3,
        })
        .setOrigin(0, 0.5)
        .setVisible(false);
      chromeKids.push(stunIcon);
    } else {
      // Non-boss enemies (add/charger/brute): 맹공 under float HP.
      // Colossus uses elite HUD status line only (no UnitView float chrome).
      const escPos = enemyEscalateBadgeLocalPos({ hpY, barH });
      escalateBadge = this.scene.add
        .text(escPos.x, escPos.y, enemyEscalateBadgeText(0), {
          fontFamily: "Segoe UI Symbol, Apple Color Emoji, sans-serif",
          fontSize: "10px",
          color: "#ffd0b8",
          stroke: "#1a0c08",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 0.5)
        .setVisible(false);
      chromeKids.push(escalateBadge);
    }
    const root = this.scene.add
      .container(this.wx(u.x), this.wy(u.y), [rig.container])
      .setDepth(RaidDepth.unitIdle);
    const chrome = this.scene.add
      .container(this.wx(u.x), this.wy(u.y), chromeKids)
      .setDepth(RaidDepth.unitChrome);
    const shCfg =
      (RIG.byRole as Record<string, { shadowRx?: number; shadowRy?: number }>)[
        art.role
      ] ?? RIG.defaults;
    const groundShadow = this.scene.add
      .ellipse(
        this.wx(u.x),
        this.wy(u.y),
        (shCfg.shadowRx ?? 11) * 2.2,
        (shCfg.shadowRy ?? 4.5) * 2.2,
        0x000000,
        0.26,
      )
      .setVisible(false)
      .setDepth(RaidDepth.unitIdle - 1);
    hpBg.setVisible(false);
    hpGhost.setVisible(false);
    hpFill.setVisible(false);
    skillBg?.setVisible(u.team === 0 && u.skillCdMax > 0);
    skillFill?.setVisible(u.team === 0 && u.skillCdMax > 0);
    this.views.set(u.id, {
      root,
      chrome,
      body,
      rig,
      groundShadow,
      hpBg,
      hpGhost,
      hpFill,
      skillBg,
      skillFill,
      stunIcon,
      escalateBadge,
      hpY,
      baseSize,
      barW,
      role: art.role,
      animRole: art.role,
      affiliationKey: aff,
      team: u.team,
      dying: false,
      deathAnimTime: 0,
      deathDuration: raidDeathFx().unitDeathSec,
      knockTrailAcc: 0,
    });
  }

  private playHeal(
    state: RaidBattleState,
    ev: Extract<RaidSimEvent, { kind: typeof RaidEvent.Heal }>,
  ): void {
    const healFx = (
      raidCatalog.view as {
        healFx?: {
          castPreset?: string;
          linkPreset?: string;
          impactPreset?: string;
        };
      }
    ).healFx;
    const castPreset = healFx?.castPreset ?? "raid.heal.cast";
    const linkPreset = healFx?.linkPreset ?? "raid.heal.link";
    const impactPreset = healFx?.impactPreset ?? "raid.heal.impact";
    const healer = state.units.find((u) => u.id === ev.healerId);
    let ox = healer?.x ?? ev.x;
    let oy = healer?.y ?? ev.y;
    if (healer) {
      const av = this.views.get(healer.id);
      if (av) {
        const muzzle = av.rig.muzzleWorld(this.toLocal(av.root));
        ox = muzzle.x;
        oy = muzzle.y;
      }
    }
    // Receiver body (display root), not sim land/feet — rising sparkles + float.
    const victim = this.views.get(ev.targetId);
    const victimLocal = victim ? this.toLocal(victim.root) : null;
    const ix = victimLocal?.x ?? ev.x;
    const iy =
      (victimLocal?.y ?? ev.y) - (victim ? victim.baseSize * 0.2 : 0);
    const angle = Math.atan2(iy - oy, ix - ox);
    // Cast at staff muzzle is secondary punctuation.
    this.playVfx(castPreset, {
      x: ox,
      y: oy,
      angle,
      ...VFX_LAYER,
    });
    // Life-link: green wave along caster→target segment (not a flying orb).
    this.playVfx(linkPreset, {
      x: ox,
      y: oy,
      x1: ix,
      y1: iy,
      angle,
      ...VFX_LAYER,
    });
    this.playVfx(impactPreset, {
      x: ix,
      y: iy,
      angle,
      targets: victim
        ? {
            victim: victim.root,
            victimBody: victim.body,
            victimRig: victim.rig.container,
          }
        : undefined,
      vars: { amount: ev.amount },
      ...VFX_LAYER,
    });
  }

  private playHit(
    state: RaidBattleState,
    ev: Extract<RaidSimEvent, { kind: typeof RaidEvent.Hit }>,
  ): void {
    const attacker =
      ev.attackerId != null
        ? state.units.find((u) => u.id === ev.attackerId)
        : undefined;
    const victimUnit =
      ev.targetKind === "unit" && ev.targetId != null
        ? state.units.find((u) => u.id === ev.targetId)
        : undefined;
    const victim =
      victimUnit != null ? this.views.get(victimUnit.id) : undefined;

    // Party units taking damage (boss AA/AoE, add AA): immediate taken reaction.
    // Do NOT route through delayed playTimedAttackFx — AoE Hit coords are knock
    // land points, so float/flash would miss the body and HP chrome looked stale.
    if (victimUnit?.team === 0 && victim) {
      if (this.hitBudget > 0) this.hitBudget -= 1;
      const role = attacker
        ? this.artForUnit(attacker).role
        : "raid_boss";
      const fx = resolveAttackFx(ATTACK_FX, role);
      const angle = Math.atan2(ev.y1 - ev.y0, ev.x1 - ev.x0);
      const hit = this.toLocal(victim.root);
      const hitX = hit.x;
      const hitY = hit.y;
      const targets = {
        victim: victim.root,
        victimBody: victim.body,
        victimRig: victim.rig.container,
      };
      this.playVfx(resolveHitPreset(fx, "ally"), {
        x: hitX,
        y: hitY,
        angle,
        targets,
        vars: { damage: ev.damage },
        ...VFX_LAYER,
      });
      // Leap-tier knock: extra impact punctuation (taken already has flash+float).
      if ((victimUnit.knock?.apex ?? 0) >= KNOCK_HEAVY_APEX) {
        this.playVfx("raid.hit.knockHeavy", {
          x: hitX,
          y: hitY,
          angle,
          targets,
          ...VFX_LAYER,
        });
      }
      return;
    }

    const role = attacker
      ? this.artForUnit(attacker).role
      : "raid_boss";
    const baseFx = resolveAttackFx(ATTACK_FX, role);
    const isMarkedShot = ev.skillId === "marked_shot";
    const isShieldBash = ev.skillId === "shield_bash";
    const skillKit = isMarkedShot
      ? markedShotFx()
      : isShieldBash
        ? shieldBashFx()
        : null;
    if (isShieldBash && attacker) {
      const av = this.views.get(attacker.id);
      av?.rig.setAttackStyle("bash");
    }
    const fx: AttackFxRoleConfig = skillKit
      ? {
          ...baseFx,
          delivery: (skillKit.delivery ?? baseFx.delivery) as AttackFxRoleConfig["delivery"],
          impactFrac: skillKit.impactFrac ?? baseFx.impactFrac,
          speedCellsPerSec:
            skillKit.speedCellsPerSec ?? baseFx.speedCellsPerSec,
          travelMinSec: skillKit.travelMinSec ?? baseFx.travelMinSec,
          travelMaxSec: skillKit.travelMaxSec ?? baseFx.travelMaxSec,
          originPreset:
            skillKit.originPreset ??
            (isMarkedShot ? "raid.marked_shot.origin" : baseFx.originPreset),
          flightPreset:
            skillKit.flightPreset ??
            (isMarkedShot ? "raid.marked_shot.flight" : baseFx.flightPreset),
          hitPreset:
            skillKit.hitPreset ??
            (isMarkedShot ? "raid.marked_shot.hit" : baseFx.hitPreset),
          takenHitPreset:
            skillKit.takenHitPreset ?? baseFx.takenHitPreset,
          spriteFxId: skillKit.spriteFxId ?? baseFx.spriteFxId,
          spriteFxAt: skillKit.spriteFxAt ?? baseFx.spriteFxAt,
          spriteFxPackId: skillKit.spriteFxPackId ?? baseFx.spriteFxPackId,
        }
      : baseFx;
    const boltScale =
      isMarkedShot && skillKit && "boltScale" in skillKit
        ? ((skillKit as MarkedShotSkillFx).boltScale ?? 1)
        : 1;
    const markPreset =
      isMarkedShot && skillKit && "markPreset" in skillKit
        ? ((skillKit as MarkedShotSkillFx).markPreset ??
          "raid.marked_shot.mark")
        : "raid.marked_shot.mark";
    const isAllyBolt = attacker?.team === 0 && fx.delivery === "bolt";
    const budgetOk = this.hitBudget > 0;
    if (budgetOk) this.hitBudget -= 1;
    // Ally bolts always get a projectile; full particle kit only while budget remains.
    // Marked shot / shield bash always play full kit (distinct from AA; rare enough).
    if (!budgetOk && !isAllyBolt && !isMarkedShot && !isShieldBash) return;

    let muzzle: { x: number; y: number } | null = null;
    if (attacker) {
      const av = this.views.get(attacker.id);
      if (av) {
        muzzle = av.rig.muzzleWorld(this.toLocal(av.root));
      }
    } else {
      muzzle = this.bossRig?.muzzleWorld(this.toLocal(this.bossRoot)) ?? null;
    }
    const { x: ox, y: oy } = resolveAttackOrigin(ev.x0, ev.y0, muzzle);
    // Boss: float + hit FX at rim toward attacker (not boss center).
    let impactX = ev.x1;
    let impactY = ev.y1;
    if (ev.targetKind === "boss") {
      const hit = bossHitImpactPos(
        state.boss.x,
        state.boss.y,
        state.boss.radius,
        ox,
        oy,
      );
      impactX = hit.x;
      impactY = hit.y;
    }
    const angle = Math.atan2(impactY - oy, impactX - ox);
    const affiliation = affiliationForBinaryTeam(
      attacker?.team === 1 || !attacker ? 1 : 0,
    );
    const swingSec = attacker
      ? swingSecForClass(attacker.classId)
      : swingSecForBoss();
    const distCells = Math.hypot(impactX - ox, impactY - oy) / 56;
    const catalogSec = attackTravelSec(fx, distCells, swingSec);
    const boltMs = visualTravelMs(ox, oy, impactX, impactY, {
      kind: "bolt",
      mul: isMarkedShot ? 0.4 : 0.45,
      minMs: isMarkedShot ? 100 : 90,
      maxMs: isMarkedShot ? 300 : 280,
    });
    const travelOverrideSec =
      fx.delivery === "bolt"
        ? Math.max(catalogSec, boltMs / 1000)
        : undefined;

    const colors = raidCatalog.view.colors as {
      boltAlly: string;
      boltHealer?: string;
      boltMarkedShot?: string;
    };
    const boltColor = hexToColor(
      isMarkedShot
        ? (colors.boltMarkedShot ?? "#ff6a2a")
        : role === "raid_healer"
          ? (colors.boltHealer ?? "#b070ff")
          : colors.boltAlly,
    );

    const playMarkLinger = () => {
      if (!isMarkedShot) return;
      this.playVfx(markPreset, {
        x: impactX,
        y: impactY,
        angle,
        targets: victim
          ? {
              victim: victim.root,
              victimBody: victim.body,
              victimRig: victim.rig.container,
            }
          : undefined,
        ...VFX_LAYER,
      });
    };

    if (!budgetOk && isAllyBolt && !isMarkedShot) {
      const travelMs = Math.round((travelOverrideSec ?? catalogSec) * 1000);
      this.spawnBolt(ox, oy, impactX, impactY, travelMs, boltColor, undefined, boltScale);
      return;
    }

    playTimedAttackFx({
      scene: this.scene,
      vfx: this.vfx,
      fx,
      affiliation,
      packId: fx.spriteFxPackId ?? "raid",
      originX: this.wx(ox),
      originY: this.wy(oy),
      impactX: this.wx(impactX),
      impactY: this.wy(impactY),
      angle,
      swingSec,
      cellPx: 56,
      travelOverrideSec,
      depth: RaidDepth.fx,
      floatDepth: RaidDepth.float,
      hitTargets: victim
        ? {
            victim: victim.root,
            victimBody: victim.body,
            victimRig: victim.rig.container,
          }
        : undefined,
      damage: ev.damage,
      targetKind: ev.targetKind === "boss" ? "structure" : "enemy",
      onHit: playMarkLinger,
      onBoltFlight:
        fx.delivery === "bolt"
          ? ({ travelMs, playHit }) => {
              this.spawnBolt(
                ox,
                oy,
                impactX,
                impactY,
                travelMs,
                boltColor,
                playHit,
                boltScale,
              );
            }
          : undefined,
    });
  }

  private spawnBolt(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    ms: number,
    color: number,
    onComplete?: () => void,
    scale = 1,
  ): void {
    const angle = Math.atan2(y1 - y0, x1 - x0);
    const s = Math.max(0.5, scale);
    const bolt = this.scene.add
      .ellipse(this.wx(x0), this.wy(y0), 16 * s, 7 * s, color, 1)
      .setDepth(RaidDepth.fx)
      .setRotation(angle);
    const duration = Math.max(16, Math.round(ms));
    this.scene.tweens.add({
      targets: bolt,
      x: this.wx(x1),
      y: this.wy(y1),
      duration,
      ease: "Cubic.easeOut",
      onComplete: () => {
        bolt.destroy();
        onComplete?.();
      },
    });
  }

  private drawTelegraph(state: RaidBattleState): void {
    this.overlay.clear();
    const colors = raidCatalog.view.colors;
    const yellow = hexToColor(colors.telegraphYellow ?? colors.telegraph);
    const red = hexToColor(colors.telegraphRed ?? "#ff3b4a");
    const fillCfg = (
      raidCatalog.view as {
        telegraphFill?: {
          fillAlpha?: number;
          outlineAlpha?: number;
          ghostOutlineAlpha?: number;
          coneSegments?: number;
        };
      }
    ).telegraphFill;
    const fillAlpha = Number(fillCfg?.fillAlpha) || 0.42;
    const outlineAlpha = Number(fillCfg?.outlineAlpha) || 0.95;
    const ghostAlpha = Number(fillCfg?.ghostOutlineAlpha) || 0.32;
    const coneSegs = Math.max(3, Number(fillCfg?.coneSegments) || 16);

    // Boss + every elite telegraph share one overlay. Do NOT early-return after
    // the first draw — that cut charge corridors mid-windup whenever a boss
    // slam/brass/leap or another elite leap won the exclusivity race.
    const leap = state.boss.leap;
    if (
      !isBossDormant(state) &&
      leap &&
      (leap.phase === "windup" || leap.phase === "travel")
    ) {
      const leapDef = demoBossDef().leap;
      const progress = leapTelegraphFillProgress({
        phase: leap.phase,
        remain: leap.remain,
        phaseDuration: leap.duration,
        windupSec: leapDef.windupSec,
        travelSec: leapDef.travelSec,
      });
      const color = leap.danger === "red" ? red : yellow;
      const lineW = leap.danger === "red" ? 4 : 3;
      this.drawCircleTelegraphFill(
        leap.landX,
        leap.landY,
        leap.landRadius,
        progress,
        color,
        lineW,
        fillAlpha,
        outlineAlpha,
        ghostAlpha,
      );
    } else if (!isBossDormant(state) && state.boss.telegraph) {
      const tg = state.boss.telegraph;
      const color = tg.danger === "red" ? red : yellow;
      const lineW = tg.danger === "red" ? 3.5 : 2;
      const progress = telegraphFillProgress(tg.remain, tg.duration);
      if (tg.shape === "circle") {
        this.drawCircleTelegraphFill(
          state.boss.x,
          state.boss.y,
          tg.range,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
        );
      } else if (tg.shape === "cone") {
        this.drawConeTelegraphFill(
          state.boss.x,
          state.boss.y,
          tg.aimAngle,
          tg.range,
          tg.coneDeg,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
          coneSegs,
        );
      } else if (tg.shape === "line") {
        this.drawLineTelegraphFill(
          state.boss.x,
          state.boss.y,
          tg.aimAngle,
          tg.range,
          tg.halfWidth ?? 20,
          tg.originPad ?? 0,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
        );
      } else {
        const _exhaustive: never = tg.shape;
        void _exhaustive;
      }
    }

    // Elite brute / charger telegraphs. Charge: line corridor for windup+travel
    // (anchored at from→land — never tip circle, never despawn mid-dash).
    for (const u of state.units) {
      if (
        (u.defId !== "brute" && u.defId !== "charger") ||
        u.hp <= 0 ||
        !u.combatArmed
      ) {
        continue;
      }
      if (
        u.leap &&
        u.leap.purpose !== "charge" &&
        (u.leap.phase === "windup" || u.leap.phase === "travel")
      ) {
        const skills = (
          raidCatalog.assault.scripts as Record<
            string,
            {
              bruteSkills: {
                leap: { windupSec: number; travelSec: number };
              };
            }
          >
        )[state.encounter.scriptId]?.bruteSkills.leap;
        const progress = leapTelegraphFillProgress({
          phase: u.leap.phase,
          remain: u.leap.remain,
          phaseDuration: u.leap.duration,
          windupSec: skills?.windupSec ?? 1,
          travelSec: skills?.travelSec ?? 0.35,
        });
        const color = u.leap.danger === "red" ? red : yellow;
        this.drawCircleTelegraphFill(
          u.leap.landX,
          u.leap.landY,
          u.leap.landRadius,
          progress,
          color,
          u.leap.danger === "red" ? 4 : 3,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
        );
        continue;
      }
      // Charge path — draw while leap lives (telegraph may be held through travel).
      if (u.leap?.purpose === "charge") {
        const chargeLeap = u.leap;
        const dx = chargeLeap.landX - chargeLeap.fromX;
        const dy = chargeLeap.landY - chargeLeap.fromY;
        const len = Math.hypot(dx, dy);
        if (Number.isFinite(len) && len > 1e-3) {
          const aim = Math.atan2(dy, dx);
          const color = chargeLeap.danger === "red" ? red : yellow;
          const lineW = chargeLeap.danger === "red" ? 3.5 : 3;
          const tg = u.telegraph;
          // Travel holds remain=0 (fill complete). Windup grows with remain.
          const progress = tg
            ? telegraphFillProgress(tg.remain, tg.duration)
            : 1;
          raidFreezeDiag.info("draw_charge_telegraph", {
            chargerId: u.id,
            drawnShape: "line",
            phase: chargeLeap.phase,
            range: len,
            halfWidth: chargeLeap.landRadius,
            progress,
            fromX: chargeLeap.fromX,
            fromY: chargeLeap.fromY,
            landX: chargeLeap.landX,
            landY: chargeLeap.landY,
            bodyX: u.x,
            bodyY: u.y,
            telegraph: tg != null,
          });
          this.drawLineTelegraphFill(
            chargeLeap.fromX,
            chargeLeap.fromY,
            aim,
            len,
            chargeLeap.landRadius,
            0,
            progress,
            color,
            lineW,
            fillAlpha,
            outlineAlpha,
            ghostAlpha,
          );
        }
        continue;
      }
      const tg = u.telegraph;
      if (!tg) continue;
      const color = tg.danger === "red" ? red : yellow;
      const progress = telegraphFillProgress(tg.remain, tg.duration);
      const lineW =
        tg.danger === "red" ? 3.5 : tg.skillId === "charge" ? 3 : 2;
      if (tg.shape === "line") {
        this.drawLineTelegraphFill(
          u.x,
          u.y,
          tg.aimAngle,
          tg.range,
          tg.halfWidth ?? 20,
          tg.originPad ?? 0,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
        );
      } else if (tg.shape === "cone") {
        this.drawConeTelegraphFill(
          u.x,
          u.y,
          tg.aimAngle,
          tg.range,
          tg.coneDeg,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
          coneSegs,
        );
      } else if (tg.shape === "circle") {
        this.drawCircleTelegraphFill(
          u.x,
          u.y,
          tg.range,
          progress,
          color,
          lineW,
          fillAlpha,
          outlineAlpha,
          ghostAlpha,
        );
      } else {
        const _exhaustive: never = tg.shape;
        void _exhaustive;
      }
    }
  }

  /** Circle AttackRange: ghost full radius + radial fill from center. */
  private drawCircleTelegraphFill(
    cx: number,
    cy: number,
    radius: number,
    progress: number,
    color: number,
    lineW: number,
    fillAlpha: number,
    outlineAlpha: number,
    ghostAlpha: number,
  ): void {
    const p = Math.min(1, Math.max(0, progress));
    this.overlay.lineStyle(lineW, color, ghostAlpha);
    this.overlay.strokeCircle(cx, cy, radius);
    const rFill = radius * p;
    if (rFill > 0.5) {
      this.overlay.fillStyle(color, fillAlpha);
      this.overlay.fillCircle(cx, cy, rFill);
      this.overlay.lineStyle(Math.max(1.5, lineW - 0.5), color, outlineAlpha);
      this.overlay.strokeCircle(cx, cy, rFill);
    }
    if (p >= 0.999) {
      this.overlay.lineStyle(lineW, color, outlineAlpha);
      this.overlay.strokeCircle(cx, cy, radius);
    }
  }

  /** Cone AttackRange: ghost full cone + radial fill from boss origin. */
  private drawConeTelegraphFill(
    x: number,
    y: number,
    aimAngle: number,
    range: number,
    coneDeg: number,
    progress: number,
    color: number,
    lineW: number,
    fillAlpha: number,
    outlineAlpha: number,
    ghostAlpha: number,
    segments: number,
  ): void {
    const p = Math.min(1, Math.max(0, progress));
    this.strokeConePath(
      coneTelegraphPoints(x, y, aimAngle, range, coneDeg, segments),
      color,
      lineW,
      ghostAlpha,
    );
    const fillRange = range * p;
    if (fillRange > 0.5) {
      const fillPts = coneTelegraphPoints(
        x,
        y,
        aimAngle,
        fillRange,
        coneDeg,
        segments,
      );
      this.overlay.fillStyle(color, fillAlpha);
      this.fillConePath(fillPts);
      this.strokeConePath(
        fillPts,
        color,
        Math.max(1.5, lineW - 0.5),
        outlineAlpha,
      );
    }
    if (p >= 0.999) {
      this.strokeConePath(
        coneTelegraphPoints(x, y, aimAngle, range, coneDeg, segments),
        color,
        lineW,
        outlineAlpha,
      );
    }
  }

  /**
   * Line charge telegraph: ghost full corridor from head → tip, fill grows along aim.
   */
  private drawLineTelegraphFill(
    bodyX: number,
    bodyY: number,
    aimAngle: number,
    range: number,
    halfWidth: number,
    originPad: number,
    progress: number,
    color: number,
    lineW: number,
    fillAlpha: number,
    outlineAlpha: number,
    ghostAlpha: number,
  ): void {
    const p = Math.min(1, Math.max(0, progress));
    const ox = bodyX + Math.cos(aimAngle) * originPad;
    const oy = bodyY + Math.sin(aimAngle) * originPad;
    const ghost = lineTelegraphQuad(ox, oy, aimAngle, range, halfWidth, 1);
    this.strokeConePath(ghost, color, lineW, ghostAlpha);
    if (p > 0.01) {
      const fill = lineTelegraphQuad(ox, oy, aimAngle, range, halfWidth, p);
      if (fill.length >= 4) {
        this.overlay.fillStyle(color, fillAlpha);
        this.fillConePath(fill);
        this.strokeConePath(
          fill,
          color,
          Math.max(1.5, lineW - 0.5),
          outlineAlpha,
        );
      }
    }
    if (p >= 0.999) {
      this.strokeConePath(ghost, color, lineW, outlineAlpha);
    }
  }

  private fillConePath(pts: Pt[]): void {
    if (pts.length < 2) return;
    this.overlay.beginPath();
    this.overlay.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) {
      this.overlay.lineTo(pts[i]!.x, pts[i]!.y);
    }
    this.overlay.closePath();
    this.overlay.fillPath();
  }

  private strokeConePath(
    pts: Pt[],
    color: number,
    lineW: number,
    alpha: number,
  ): void {
    if (pts.length < 2) return;
    this.overlay.lineStyle(lineW, color, alpha);
    this.overlay.beginPath();
    this.overlay.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) {
      this.overlay.lineTo(pts[i]!.x, pts[i]!.y);
    }
    this.overlay.closePath();
    this.overlay.strokePath();
  }

  private drawAggroArrow(state: RaidBattleState): void {
    this.aggroGfx.clear();
    if (this.bossDying || state.phase !== "playing" || isBossDormant(state)) {
      return;
    }
    const target = resolveBossAggroHudTarget(
      state.boss,
      state.units,
      state.time,
    );
    if (!target) return;

    const colors = raidCatalog.view.colors as { aggroArrow?: string };
    const disp = (
      raidCatalog.view.display as {
        aggroArrow?: {
          lineWidth?: number;
          headLength?: number;
          headWidth?: number;
          alpha?: number;
          insetFrom?: number;
          insetTo?: number;
        };
      }
    ).aggroArrow;
    const color = hexToColor(colors.aggroArrow ?? "#ff3b4a");
    const alpha = Math.max(0, Math.min(1, Number(disp?.alpha) || 0.72));
    const lineW = Math.max(1, Number(disp?.lineWidth) || 3.5);
    const headLen = Math.max(4, Number(disp?.headLength) || 14);
    const headW = Math.max(3, Number(disp?.headWidth) || 8);
    const insetFrom = Math.max(0, Number(disp?.insetFrom) || 36);
    const insetTo = Math.max(0, Number(disp?.insetTo) || 20);

    const x0 = state.boss.x;
    const y0 = state.boss.y;
    const x1 = target.x;
    const y1 = target.y;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (len < insetFrom + insetTo + 4) return;
    const ux = dx / len;
    const uy = dy / len;
    const sx = x0 + ux * insetFrom;
    const sy = y0 + uy * insetFrom;
    const ex = x1 - ux * insetTo;
    const ey = y1 - uy * insetTo;

    this.aggroGfx.lineStyle(lineW, color, alpha);
    this.aggroGfx.lineBetween(sx, sy, ex, ey);
    const ang = Math.atan2(uy, ux);
    const ax = ex - Math.cos(ang) * headLen;
    const ay = ey - Math.sin(ang) * headLen;
    this.aggroGfx.fillStyle(color, alpha);
    this.aggroGfx.beginPath();
    this.aggroGfx.moveTo(ex, ey);
    this.aggroGfx.lineTo(
      ax + Math.cos(ang + Math.PI / 2) * headW,
      ay + Math.sin(ang + Math.PI / 2) * headW,
    );
    this.aggroGfx.lineTo(
      ax + Math.cos(ang - Math.PI / 2) * headW,
      ay + Math.sin(ang - Math.PI / 2) * headW,
    );
    this.aggroGfx.closePath();
    this.aggroGfx.fillPath();
  }

  private drawGestureLayer(state: RaidBattleState): void {
    this.gestureGfx.clear();
    this.pinGfx.clear();
    const colors = raidCatalog.view.colors;
    const area = hexToColor(
      (colors as { gestureArea?: string }).gestureArea ?? "#5ad4ff",
    );
    const arrow = hexToColor(
      (colors as { gestureArrow?: string }).gestureArrow ?? "#ffe08a",
    );
    const pin = hexToColor(
      (colors as { gesturePin?: string }).gesturePin ?? "#ffd27a",
    );
    const select = hexToColor(
      (colors as { gestureSelect?: string }).gestureSelect ?? "#ffe66a",
    );
    const red = hexToColor(colors.telegraphRed ?? "#ff3b4a");
    const v = this.gestureVisual;

    if (v.kind === "areaGrow" || v.kind === "areaAim") {
      const pulse = v.kind === "areaGrow" ? 0.1 + 0.08 * v.pulse : 0.14;
      this.gestureGfx.fillStyle(area, pulse);
      this.gestureGfx.lineStyle(2, area, 0.85);
      this.gestureGfx.fillCircle(v.cx, v.cy, v.r);
      this.gestureGfx.strokeCircle(v.cx, v.cy, v.r);
    }
    // Feet select rings under heroes in dash / groupDash aim (below units).
    this.drawGestureSelectRings(state, v, select);
    if (v.kind === "unitAim" || v.kind === "areaAim") {
      const alpha = v.valid ? 0.95 : 0.35;
      this.drawAimArrow(v.x0, v.y0, v.x1, v.y1, arrow, alpha);
      const dx = v.x1 - v.x0;
      const dy = v.y1 - v.y0;
      const len = Math.hypot(dx, dy) || 1;
      const maxDist =
        Number(
          (raidCatalog.balance as { gesture?: { dashMaxDist?: number } }).gesture
            ?.dashMaxDist,
        ) || 220;
      const dist = Math.min(maxDist, len);
      const gx = v.x0 + (dx / len) * dist;
      const gy = v.y0 + (dy / len) * dist;
      this.gestureGfx.lineStyle(1.5, arrow, alpha * 0.8);
      this.gestureGfx.strokeCircle(gx, gy, 12);
    }
    if (v.kind === "pin") {
      this.drawPinRing(v.x, v.y, pin, red, state);
    }

    // Active dash destinations (order markers).
    for (const u of state.units) {
      if (u.team !== 0 || u.hp <= 0) continue;
      const o = u.brain.order.current;
      if (!o || o.kind !== "dash" || o.x == null || o.y == null) continue;
      this.gestureGfx.lineStyle(1.5, arrow, 0.55);
      this.gestureGfx.strokeCircle(o.x, o.y, 10);
    }
    // Live pin rings for any pinned units (in case visual lags).
    for (const u of state.units) {
      if (u.team !== 0 || u.hp <= 0) continue;
      const o = u.brain.order.current;
      if (!o || o.kind !== "pin") continue;
      if (v.kind === "pin" && v.unitId === u.id) continue;
      this.drawPinRing(u.x, u.y, pin, red, state);
    }
  }

  private drawGestureSelectRings(
    state: RaidBattleState,
    v: GestureVisual,
    color: number,
  ): void {
    const ids = gestureSelectTargets(v, state.units);
    if (ids.length === 0) return;
    const disp = raidCatalog.view.display as {
      gestureSelectRingMin?: number;
      gestureSelectRingMul?: number;
    };
    const ringMin = Number(disp.gestureSelectRingMin) || 14;
    const ringMul = Number(disp.gestureSelectRingMul) || 1.25;
    const aimValid =
      v.kind === "unitAim" || v.kind === "areaAim" ? v.valid : true;
    const strokeA = aimValid ? 0.92 : 0.45;
    const fillA = aimValid ? 0.14 : 0.07;
    const idSet = new Set(ids);
    for (const u of state.units) {
      if (!idSet.has(u.id)) continue;
      const r = Math.max(ringMin, u.radius * ringMul);
      this.gestureGfx.fillStyle(color, fillA);
      this.gestureGfx.fillCircle(u.x, u.y, r);
      this.gestureGfx.lineStyle(2.5, color, strokeA);
      this.gestureGfx.strokeCircle(u.x, u.y, r);
    }
  }

  private drawAimArrow(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    color: number,
    alpha: number,
  ): void {
    this.gestureGfx.lineStyle(3, color, alpha);
    this.gestureGfx.lineBetween(x0, y0, x1, y1);
    const ang = Math.atan2(y1 - y0, x1 - x0);
    const hs = 10;
    const ax = x1 - Math.cos(ang) * hs;
    const ay = y1 - Math.sin(ang) * hs;
    this.gestureGfx.fillStyle(color, alpha);
    this.gestureGfx.beginPath();
    this.gestureGfx.moveTo(x1, y1);
    this.gestureGfx.lineTo(
      ax + Math.cos(ang + Math.PI / 2) * 6,
      ay + Math.sin(ang + Math.PI / 2) * 6,
    );
    this.gestureGfx.lineTo(
      ax + Math.cos(ang - Math.PI / 2) * 6,
      ay + Math.sin(ang - Math.PI / 2) * 6,
    );
    this.gestureGfx.closePath();
    this.gestureGfx.fillPath();
  }

  private drawPinRing(
    x: number,
    y: number,
    pinColor: number,
    redColor: number,
    state: RaidBattleState,
  ): void {
    const inRed =
      (state.boss.leap?.danger === "red" &&
        Math.hypot(x - (state.boss.leap?.landX ?? 0), y - (state.boss.leap?.landY ?? 0)) <
          (state.boss.leap?.landRadius ?? 0)) ||
      (state.boss.telegraph?.danger === "red" &&
        pointInBossDanger(x, y, state));
    const blink = inRed ? 0.45 + 0.45 * Math.sin(state.time * 14) : 0.9;
    const color = inRed ? redColor : pinColor;
    this.pinGfx.lineStyle(3, color, blink);
    this.pinGfx.strokeCircle(x, y, 18);
    this.pinGfx.lineStyle(2, color, blink * 0.85);
    this.pinGfx.lineBetween(x - 6, y + 2, x - 2, y + 6);
    this.pinGfx.lineBetween(x - 2, y + 6, x + 7, y - 5);
  }
}

function pointInBossDanger(x: number, y: number, state: RaidBattleState): boolean {
  const tg = state.boss.telegraph;
  if (!tg || tg.danger !== "red") return false;
  if (tg.shape === "circle") {
    return Math.hypot(x - state.boss.x, y - state.boss.y) <= tg.range;
  }
  // Cone: approximate with range + angle check.
  const dx = x - state.boss.x;
  const dy = y - state.boss.y;
  const d = Math.hypot(dx, dy);
  if (d > tg.range || d < 1e-3) return false;
  const ang = Math.atan2(dy, dx);
  let diff = ang - tg.aimAngle;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return Math.abs(diff) <= ((tg.coneDeg * Math.PI) / 180) * 0.5;
}
